/**
 * NotYetEnforcedNotice — shared inline notice for config fields the engine
 * currently ignores.
 *
 * One component so that removing badges later (as the engine catches up) is a
 * one-line change per field. Two variants:
 *  - `badge` (default): compact amber pill with a tooltip carrying the full
 *    explanation. Place it next to a field label.
 *  - `block`: full-width amber note for page/section-level notices.
 */

import { AlertTriangle } from 'lucide-react';

export interface NotYetEnforcedNoticeProps {
  /** Compact label shown on the badge. Defaults to "Not yet enforced". */
  label?: string;
  /** Tooltip / block text. Defaults to the standard copy. */
  tooltip?: string;
  /** Render variant. `badge` = inline pill, `block` = full-width note. */
  variant?: 'badge' | 'block';
  className?: string;
}

const DEFAULT_TOOLTIP =
  'Not yet enforced by the engine — configuring this has no runtime effect.';

export function NotYetEnforcedNotice({
  label = 'Not yet enforced',
  tooltip = DEFAULT_TOOLTIP,
  variant = 'badge',
  className,
}: NotYetEnforcedNoticeProps) {
  if (variant === 'block') {
    return (
      <div
        className={`flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 ${
          className ?? ''
        }`}
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
        <span>{tooltip}</span>
      </div>
    );
  }

  return (
    <span
      title={tooltip}
      className={`inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 ${
        className ?? ''
      }`}
    >
      <AlertTriangle className="h-3 w-3 flex-shrink-0 text-amber-600" />
      {label}
    </span>
  );
}
